import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Content } from '../types/content';
import { useContent } from '../hooks/useContent';
import { useTheme } from '../contexts/ThemeContext';
import ContentViewer from '../components/content/ContentViewer';
import CommentList from '../components/comments/CommentList';
import SEO from '../components/SEO';

export default function ContentPage() {
  const { type, id } = useParams<{ type: string; id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentText, setCommentText] = useState('');

  const {
    content,
    comments,
    isLoading,
    error,
    fetchContent,
    fetchComments,
    addComment,
    likeContent,
    likeComment,
  } = useContent({
    contentType: type as 'post' | 'article' | 'video',
    contentId: id || '',
  });

  useEffect(() => {
    if (type && id) {
      fetchContent();
      fetchComments();
    }
  }, [type, id, fetchContent, fetchComments]);

  const handleShare = async () => {
    if (content) {
      try {
        await navigator.share({
          title: 'content' in content ? content.title : 'Share this content',
          text: 'content' in content ? content.description : content.content,
          url: window.location.href,
        });
      } catch (err) {
        navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      }
    }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;

    try {
      await addComment({
        content: commentText,
        contentId: id || '',
        contentType: type as 'post' | 'article' | 'video',
        author: {
          id: 'current-user',
          name: 'Current User',
          avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
          role: 'user',
        },
      });
      setCommentText('');
      setShowCommentForm(false);
    } catch (err) {
      console.error('Failed to add comment:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className={`text-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
          <h2 className="text-2xl font-bold mb-2">Content Not Found</h2>
          <p className="mb-4">The content you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={'title' in content ? content.title : 'View Content'}
        description={'description' in content ? content.description : content.content.slice(0, 160)}
        keywords={content.tags}
        type={type as 'article' | 'website'}
        author={'author' in content ? content.author.name : undefined}
        publishedTime={content.createdAt.toISOString()}
        modifiedTime={content.updatedAt.toISOString()}
        image={'thumbnailUrl' in content ? content.thumbnailUrl : undefined}
      />

      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContentViewer
            content={content}
            onLike={likeContent}
            onComment={() => setShowCommentForm(true)}
            onShare={handleShare}
          />

          <div className="mt-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className={`text-xl font-semibold ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Comments ({comments.length})
              </h2>
              <button
                onClick={() => setShowCommentForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add Comment
              </button>
            </div>

            {showCommentForm && (
              <div className={`mb-6 p-4 rounded-lg ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              }`}>
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Write your comment..."
                  className={`
                    w-full p-3 rounded-lg border resize-none focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  rows={4}
                />
                <div className="flex justify-end space-x-3 mt-3">
                  <button
                    onClick={() => setShowCommentForm(false)}
                    className={`
                      px-4 py-2 rounded-lg transition-colors
                      ${theme === 'dark'
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }
                    `}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleComment}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    disabled={!commentText.trim()}
                  >
                    Post Comment
                  </button>
                </div>
              </div>
            )}

            <CommentList
              comments={comments}
              contentType={type as 'post' | 'article' | 'video'}
              onLike={likeComment}
              onReply={(commentId) => {
                setShowCommentForm(true);
                setCommentText(`@${comments.find(c => c.id === commentId)?.author.name} `);
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}